import type { AxiosResponse } from 'axios';
import { triggerBlobDownload } from '@srm/ui/lib/download';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import { backgroundJobKind } from '@services/backgroundJobs/backgroundJobKinds';
import { base64ToUtf8 } from '@srm/ui/lib/base64';
import type { IBackgroundJobTaskContext, IBackgroundJobWaitOutcome } from '@services/backgroundJobs/backgroundJobTask';

/**
 * Способ довести загрузку до итога. Отказ сети или HTTP — отклонённый промис: текст ошибки делает окно.
 */
export type LoadFromFileTransport = (
    file: File,
    context: IBackgroundJobTaskContext,
) => Promise<IBackgroundJobWaitOutcome>;

const decodeHeader = (value: unknown) => (typeof value === 'string' ? base64ToUtf8(value) : '');

export const resultFileName = (name: string | null | undefined): string =>
    name || backgroundJobKind('loadFromFile').wait.resultFileName;

/** Итог загрузки одним запросом, как его отдал сервер: флаг, сообщение и имя файла — в заголовках, файл — телом. */
export const parseLoadFromFileResponse = (response: AxiosResponse<Blob>): IBackgroundJobWaitOutcome => {
    const loadSuccess = response.headers['loadsuccess'];
    const blob = response.data;
    const name = resultFileName(decodeHeader(response.headers['loadfilename']));
    return {
        success: typeof loadSuccess === 'string' && loadSuccess.toLowerCase() === 'true',
        message: decodeHeader(response.headers['loadmessage']),
        file: blob && blob.size > 0 ? { name, download: () => triggerBlobDownload(blob, name) } : undefined,
    };
};

/** Загрузка одним запросом: ответ приходит, когда сервер закончил обработку. */
export const createSyncLoadFromFileTransport =
    (docId: string, actionKey: string): LoadFromFileTransport =>
    (file) =>
        new DocumentExecutionService(docId).loadFromFile(actionKey, file).then(parseLoadFromFileResponse);
